import { Injectable } from '@angular/core';
import { ApiEndpointMap, createApiEndpointTemplate } from 'app/core/services/api-endpoint-template';
import { ObjectService } from 'app/shared/services/utility/object.service';
import { EnvironmentService } from './environment.service';

@Injectable()
export class ApiEndpointService {
    private apiEndpoints!: ApiEndpointMap;

    public constructor(private readonly environmentService: EnvironmentService,
                       private readonly objectService: ObjectService,
    ) {
        this.constructEndpoints();
    }

    /**
     * A fully-resolved, type-safe map of all API endpoints as absolute URLs.
     *
     * Use this instead of `getEndpoint()` to access endpoints like:
     *
     * ```ts
     * this.apiEndpointService.endpointUrls.claims.conversations.metadata;
     * ```
     *
     * Endpoints are automatically prefixed with the appropriate base URL
     * (e.g., `v3BaseApiUrl`) unless already defined as a full URL.
     */
    public get endpointUrls(): Readonly<ApiEndpointMap> {
        return this.apiEndpoints;
    }

    /**
     * Unless you are trying to resolve a path, use `endpointUrls.xxx` instead for direct, type-safe access.
     *
     * Example:
     *
     * ❌ Old:
     * ```ts
     * apiEndpointService.getEndpoint('claims.conversations.metadata');
     * ```
     *
     * ✅ New (type-safe access! 💖):
     * ```ts
     * apiEndpointService.endpointUrls.claims.conversations.metadata;
     * ```
     *
     * That will give you a list of full URLs.
     * If you prefer to use a URL template instead, access it directly from apiEndpointTemplate:
     * ```ts
     * apiEndpointTemplate.claims.conversations.metadata;
     * ```
     *
     * ⚠️ BUT you may need to provide a base URL in SafeUrlAssembler() if the template does not already include the base URL:
     * ```ts
     * const endpoint = SafeUrlAssembler(environment.v3BaseApiUrl)
     *     .template(apiEndpointTemplate.claims.conversations.metadata)
     *     .param('claimId', claimId)
     *     .toString();
     * ```
     * 🔒 This method is kept for backward compatibility but may be removed in the future.
     */
    public getEndpoint(key: string): string {
        const endpoint: string = this.objectService.getByPath(this.apiEndpoints, key);
        if (!endpoint) {
            throw new Error(`Cannot find the endpoint named ${ key }`);
        }

        return this.ensureFullUrl(endpoint);
    }

    public constructEndpoints(): void {
        this.apiEndpoints = createApiEndpointTemplate(this.environmentService.environment);
        this.apiEndpoints = this.applyBaseUrls(this.apiEndpoints);
    }

    private ensureFullUrl(endpoint: string): string {
        if (/^https?:\/\//i.test(endpoint)) {
            return endpoint;
        }

        const baseUrl = this.environmentService.environment.v3BaseApiUrl;
        return `${ baseUrl }${ endpoint.startsWith('/') ? '' : '/' }${ endpoint }`;
    }

    private applyBaseUrls<T>(template: T): T {
        const apply = (obj: any): any =>
            Object.fromEntries(
                Object.entries(obj).map(([key, value]) => [
                    key,
                    typeof value === 'string'
                        ? this.ensureFullUrl(value)
                        : apply(value),
                ]),
            );

        return apply(template);
    }
}
